import { type FileToUpload, formatSize, isFileOverLimit, isPdf, noop, useIntl } from '@capsicum/common';
import { type ForwardedRef, forwardRef, useImperativeHandle } from 'react';
import styled, { useTheme } from 'styled-components';

import { useInfoModal } from '../../../hooks/components/useInfoModal';
import { type UploadProps, useFilesUpload } from '../../../hooks/helpers/useFilesUpload';
import { Alert, InteractionLayer } from '../../atoms';
import { AddDocumentIcon, AlertIcon, DocumentIcon, PdfIcon, RequirementExpiredIcon, TrashIcon } from '../../icons';
import { BaseFileInput } from '../../molecules/form/file-input/BaseFileInput';

type DocumentPickerUploadProps = {
	id?: string;
	isDisabled: boolean;
	onUploadedFiles: (files: Array<FileToUpload>) => void;
	showFilesList?: boolean;
	uploadProps: UploadProps;
};

/**
 * Document Picker Upload
 *
 * @param id The id of the file input
 * @param isDisabled Whether the upload is disabled
 * @param onUploadedFiles The function to call when files are uploaded
 * @param showFilesList Whether to show the list of uploaded file
 * @param uploadProps The upload properties
 *
 * @returns {JSX.Element} The Document Picker Upload Component
 */
export const DocumentPickerUpload = forwardRef(
	(
		{ id, isDisabled, onUploadedFiles, showFilesList = true, uploadProps }: DocumentPickerUploadProps,
		ref: ForwardedRef<{ removeAllFiles: () => void }>
	) => {
		const { colors } = useTheme();
		const { $t } = useIntl();
		const { showInfoModal } = useInfoModal();

		const {
			alert,
			inputRef,
			isFilesLimitReached,
			removeAllFiles,
			removeFile,
			showAlert,
			uploadedFiles,
			uploadFile
		} = useFilesUpload({
			onUploadedFiles,
			uploadProps
		});

		useImperativeHandle(ref, () => ({ removeAllFiles }));

		const { maxSize } = uploadProps;

		const showLimitReachedModal = () => {
			showInfoModal({
				buttonOnHandlers: {
					onPrimaryButtonHandler: noop
				},
				buttonTexts: {
					primaryButtonText: $t('global.button.understand')
				},
				image: 'warning',
				subtitle: $t('global.limitReachedModal.description', {
					numberOfFiles: uploadProps.selectionLimit
				}),
				testID: 'files-limit-reached',
				title: $t('global.limitReachedModal.title'),
				variant: 'alert'
			});
		};

		return (
			<>
				{uploadedFiles.length > 0 && showFilesList && (
					<S.UploadedFilesList>
						{uploadedFiles.map((file: FileToUpload, uploadedFileNamesIndex: number) => (
							<S.FileRow key={uploadedFileNamesIndex}>
								<S.FileDescription>
									{isPdf(file) ? (
										<PdfIcon height={24} width={24} />
									) : (
										<DocumentIcon height={24} width={24} />
									)}
									<S.FileName $oversized={isFileOverLimit(maxSize, file)}>{file.name}</S.FileName>
								</S.FileDescription>

								<S.FileAction>
									{isFileOverLimit(maxSize, file) && (
										<RequirementExpiredIcon color={colors.icons.error.enabled} />
									)}
									<S.Trash
										color={colors.backgrounds.main.inverse}
										onClick={() => removeFile(uploadedFileNamesIndex)}
									/>
								</S.FileAction>
							</S.FileRow>
						))}
					</S.UploadedFilesList>
				)}

				{showAlert ? (
					<div>
						<Alert
							closable={false}
							icon={AlertIcon}
							subTitle={alert.subtitle}
							testID={alert.testID}
							title={alert.title}
							type="warning"
						/>
					</div>
				) : (
					<S.AddDocumentInteractionLayer disabled={isDisabled} testID="document-picker-upload" onClick={noop}>
						<BaseFileInput
							accept=".png, .jpg, .jpeg, .pdf"
							id={id}
							multiple
							name="uploadedFiles"
							ref={inputRef}
							style={{ display: 'none' }}
							onChange={uploadFile}
						>
							<S.AddDocument
								onClick={() =>
									isFilesLimitReached
										? showLimitReachedModal()
										: document.getElementById(id || 'hiddenFileInput')?.click()
								}
							>
								<AddDocumentIcon color={colors.icons.main.unselected} height={32} width={32} />
								<S.AddDocumentText>
									{$t('postalAddressVerification.addADocument', {
										size: formatSize(maxSize)
									})}
								</S.AddDocumentText>
							</S.AddDocument>
						</BaseFileInput>
					</S.AddDocumentInteractionLayer>
				)}
			</>
		);
	}
);

DocumentPickerUpload.displayName = 'DocumentPickerUpload';

namespace S {
	export const UploadedFilesList = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const FileRow = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
	`;

	export const FileDescription = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const FileName = styled.p<{ $oversized: boolean }>`
		${({ theme }) => theme.fonts.text.text3};
		margin: 0;
		color: ${({ $oversized, theme }) =>
			$oversized ? theme.colors.text.destructive.enabled : theme.colors.text.brand.enabled};
	`;

	export const FileAction = styled.div`
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: flex-end;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const AddDocumentInteractionLayer = styled(InteractionLayer)`
		display: block;
	`;

	export const AddDocument = styled.div`
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		align-items: center;
		border: ${({ theme }) => theme.spacings.xxxxsmall} dashed ${({ theme }) => theme.colors.backgrounds.main.subtle};
		gap: ${({ theme }) => theme.spacings.xsmall};
		padding: ${({ theme }) => theme.spacings.large};
		border-radius: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const AddDocumentText = styled.p`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-bottom: 0;
	`;

	export const Trash = styled(TrashIcon)`
		cursor: pointer;
	`;
}
